import React from 'react';
import { renderTerminalSchematic } from '../../utils/terminalRenderLogic';
import { preprocessTextForWrapping, splitText, getMultipolarSeparator } from '../../utils/cableLogic';
import { normalizeColorToCanonical } from '../../utils/colorUtils';
import CableSVGPath from './CableSVGPath';

const isEarthWire = (colore) => normalizeColorToCanonical(colore) === 'YE/GN';

const buildPolylinePath = (points = []) => points
    .filter((point) => point && Number.isFinite(point.x) && Number.isFinite(point.y))
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

const renderConnectionLabel = (text, x, y, textAnchor, lineX) => {
    if (!text) return null;

    return (
        <text
            x={x}
            y={y + 3}
            className="text-xs font-medium"
            textAnchor={textAnchor}
            fontSize="10"
            fill="#374151"
        >
            {splitText(preprocessTextForWrapping(text), 8).map((line, i) => (
                <tspan
                    key={i}
                    x={lineX}
                    dy={i === 0 ? 0 : 12}
                >
                    {line}
                </tspan>
            ))}
        </text>
    );
};

const renderSiglaturaBadge = (siglatura, x, y, align) => {
    if (!siglatura || String(siglatura).trim() === '') return null;

    const width = (siglatura.length * 8) + 12;
    const rectX = align === 'left'
        ? x - 6
        : align === 'right'
            ? x - width + 6
            : x - (width / 2);
    const textAnchor = align === 'left' ? 'start' : align === 'right' ? 'end' : 'middle';

    return (
        <g>
            <rect
                x={rectX}
                y={y - 8}
                width={width}
                height={16}
                rx={3}
                fill="white"
                stroke="#D1D5DB"
                strokeWidth={1}
                filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
            />
            <text
                x={x}
                y={y + 4}
                className="text-xs font-bold fill-gray-800"
                textAnchor={textAnchor}
            >
                {siglatura}
            </text>
        </g>
    );
};

const renderIndexBadge = (index, x, y, opacity = 1) => (
    <g>
        <circle
            cx={x}
            cy={y}
            r={12}
            fill="#1F2937"
            opacity={0.95 * opacity}
        />
        <text
            x={x}
            y={y + 4}
            className="text-xs font-bold"
            fill="white"
            textAnchor="middle"
            opacity={opacity}
        >
            #{index}
        </text>
    </g>
);

const getSmartSiglaturaX = ({ centerX, terminalX, side, maxDistance = 30 }) => {
    if (side === 'A') {
        return Math.min(centerX, terminalX + maxDistance);
    }

    return Math.max(centerX, terminalX - maxDistance);
};

const countBranchNodes = (branchEntries = []) => branchEntries.reduce(
    (total, entry) => total + 1 + countBranchNodes(entry.children || []),
    0
);

const getOrderedBranchBuckets = (branchEntries = [], anchorIndex = 0) => {
    const aboveEntries = [];
    const belowEntries = [];

    branchEntries.forEach((entry) => {
        if ((entry?.cable?.originalIndex ?? 0) < anchorIndex) {
            aboveEntries.push(entry);
        } else {
            belowEntries.push(entry);
        }
    });

    return {
        aboveEntries: [...aboveEntries].sort((left, right) =>
            (right.cable?.originalIndex ?? 0) - (left.cable?.originalIndex ?? 0)
        ),
        belowEntries: [...belowEntries].sort((left, right) =>
            (left.cable?.originalIndex ?? 0) - (right.cable?.originalIndex ?? 0)
        )
    };
};

const sortPathEntriesForHover = (pathEntries = []) => (
    [...pathEntries].sort((left, right) => {
        const leftHovered = left.isHovered ? 1 : 0;
        const rightHovered = right.isHovered ? 1 : 0;
        return leftHovered - rightHovered;
    })
);

const flattenBranchRenderData = ({
    branchEntries,
    sharedSide,
    parentY,
    branchSpacing,
    joinX,
    sourceTerminalX,
    branchTerminalBaseX,
    direction,
    rowDirection = 1,
    startOffset = 0
}) => {
    if (!branchEntries || branchEntries.length === 0) {
        return [];
    }

    let rowOffset = startOffset;
    const renderData = [];

    branchEntries.forEach((entry, branchIndex) => {
        const branchCable = entry.cable;
        const branchSide = entry.side;
        const displaySide = branchSide === 'A' ? 'B' : 'A';
        rowOffset += 1;

        const branchY = parentY + (rowOffset * branchSpacing * rowDirection);
        const branchHorizontalEndX = branchTerminalBaseX + (direction * 18);

        renderData.push({
            key: `pole-branch-${branchCable.originalIndex}-${sharedSide}-${branchIndex}-${rowOffset}`,
            cable: branchCable,
            sharedSide,
            branchY,
            branchJoinX: joinX,
            branchMidY: (parentY + branchY) / 2,
            branchHorizontalEndX,
            terminal: branchCable?.[`terminale${displaySide}`],
            collegamento: branchCable?.[`collegamento${displaySide}`],
            siglatura: branchCable?.[`siglatura${displaySide}`] || branchCable.siglatura,
            pathD: buildPolylinePath([
                { x: sourceTerminalX, y: parentY },
                { x: joinX, y: parentY },
                { x: joinX, y: branchY },
                { x: branchHorizontalEndX, y: branchY }
            ])
        });

        const children = flattenBranchRenderData({
            branchEntries: entry.children || [],
            sharedSide,
            parentY: branchY,
            branchSpacing,
            joinX,
            sourceTerminalX: branchHorizontalEndX,
            branchTerminalBaseX: branchHorizontalEndX - (direction * 18),
            direction,
            rowDirection,
            startOffset: 0
        });

        renderData.push(...children);
        rowOffset += countBranchNodes(entry.children || []);
    });

    return renderData;
};

const MultipolarCableVisualization = ({ cable, hoveredCable, setHoveredCable, isMobile, settings, poleBranches = {} }) => {
    if (!cable.poli || !Array.isArray(cable.poli)) {
        return null;
    }

    const svgWidth = isMobile ? 400 : 800;
    const hasAnyBranches = cable.poli.some((_, index) => {
        const branches = poleBranches[index] || { A: [], B: [] };
        return branches.A.length > 0 || branches.B.length > 0;
    });
    const visualRowsLeft = cable.poli.reduce((total, _, index) => {
        const branches = poleBranches[index] || { A: [], B: [] };
        const { aboveEntries, belowEntries } = getOrderedBranchBuckets(branches.A, cable.originalIndex);
        return total + 1 + countBranchNodes(aboveEntries) + countBranchNodes(belowEntries);
    }, 0);
    const visualRowsRight = cable.poli.reduce((total, _, index) => {
        const branches = poleBranches[index] || { A: [], B: [] };
        const { aboveEntries, belowEntries } = getOrderedBranchBuckets(branches.B, cable.originalIndex);
        return total + 1 + countBranchNodes(aboveEntries) + countBranchNodes(belowEntries);
    }, 0);
    const visualRows = Math.max(visualRowsLeft, visualRowsRight);
    const compactSvgHeight = Math.max(200, 80 + (cable.poli.length * 60));
    const expandedSvgHeight = Math.max(200, 80 + (visualRows * 60));
    const svgHeight = hasAnyBranches ? expandedSvgHeight : compactSvgHeight;
    const mainCableY = svgHeight / 2;

    const cableWidth = 300;
    const cableHeight = 40;
    const cableX = (svgWidth - cableWidth) / 2;
    const cableRect = {
        x: cableX,
        y: mainCableY - cableHeight / 2,
        width: cableWidth,
        height: cableHeight
    };

    const poloLeftX = 120;
    const poloRightX = svgWidth - 120;
    const compactPoloSpacing = Math.min(50, (svgHeight - 100) / Math.max(1, cable.poli.length - 1));
    const expandedPoloSpacing = Math.min(50, (svgHeight - 100) / Math.max(1, visualRows - 1));
    const compactPoloStartY = svgHeight / 2 - ((cable.poli.length - 1) * compactPoloSpacing) / 2;
    const poloStartYLeft = hasAnyBranches
        ? svgHeight / 2 - ((visualRowsLeft - 1) * expandedPoloSpacing) / 2
        : compactPoloStartY;
    const poloStartYRight = hasAnyBranches
        ? svgHeight / 2 - ((visualRowsRight - 1) * expandedPoloSpacing) / 2
        : compactPoloStartY;

    let currentRowLeft = 0;
    let currentRowRight = 0;
    const poleLayout = cable.poli.map((polo, index) => {
        const branches = poleBranches[index] || { A: [], B: [] };
        const leftBuckets = getOrderedBranchBuckets(branches.A, cable.originalIndex);
        const rightBuckets = getOrderedBranchBuckets(branches.B, cable.originalIndex);
        const leftAboveCount = countBranchNodes(leftBuckets.aboveEntries);
        const leftBelowCount = countBranchNodes(leftBuckets.belowEntries);
        const rightAboveCount = countBranchNodes(rightBuckets.aboveEntries);
        const rightBelowCount = countBranchNodes(rightBuckets.belowEntries);
        const poloYLeft = hasAnyBranches
            ? poloStartYLeft + ((currentRowLeft + leftAboveCount) * expandedPoloSpacing)
            : compactPoloStartY + (index * compactPoloSpacing);
        const poloYRight = hasAnyBranches
            ? poloStartYRight + ((currentRowRight + rightAboveCount) * expandedPoloSpacing)
            : compactPoloStartY + (index * compactPoloSpacing);
        const topVisualYLeft = hasAnyBranches
            ? poloYLeft - (leftAboveCount * expandedPoloSpacing)
            : poloYLeft;
        const topVisualYRight = hasAnyBranches
            ? poloYRight - (rightAboveCount * expandedPoloSpacing)
            : poloYRight;

        if (hasAnyBranches) {
            currentRowLeft += 1 + leftAboveCount + leftBelowCount;
            currentRowRight += 1 + rightAboveCount + rightBelowCount;
        }

        return {
            polo,
            index,
            poloYLeft,
            poloYRight,
            topVisualYLeft,
            topVisualYRight,
            branches,
            leftBuckets,
            rightBuckets
        };
    });

    return (
        <svg
            className="w-full"
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            style={{ backgroundColor: '#f8f9fa' }}
            onMouseLeave={() => setHoveredCable(null)}
        >
            {(() => {
                const lunghezzeUnicheA = [...new Set(cable.poli.map(p => p.lunghezzaA || 0))].filter(l => l > 0).sort((a, b) => a - b);
                const lunghezzeUnicheB = [...new Set(cable.poli.map(p => p.lunghezzaB || 0))].filter(l => l > 0).sort((a, b) => a - b);
                const maxLunghezzaA = lunghezzeUnicheA.length > 0 ? Math.max(...lunghezzeUnicheA) : 1;
                const maxLunghezzaB = lunghezzeUnicheB.length > 0 ? Math.max(...lunghezzeUnicheB) : 1;
                const maxScale = 80;
                const pathEntries = [];
                const decorationEntries = [];

                poleLayout.forEach(({ polo, index, poloYLeft, poloYRight, branches, leftBuckets, rightBuckets }) => {
                    const isHovered = hoveredCable === cable.originalIndex;
                    const cableExitXA = cableRect.x;
                    const cableExitYA = cableRect.y + (cableRect.height / (cable.poli.length + 1)) * (index + 1);
                    const lunghezzaVisualeA = ((polo.lunghezzaA || 0) / Math.max(maxLunghezzaA, 1)) * maxScale;
                    const horizontalStartXA = poloLeftX + 40;
                    const horizontalEndXA = horizontalStartXA - lunghezzaVisualeA;
                    const branchJoinXA = horizontalStartXA;

                    const cableExitXB = cableRect.x + cableRect.width;
                    const cableExitYB = cableRect.y + (cableRect.height / (cable.poli.length + 1)) * (index + 1);
                    const lunghezzaVisualeB = ((polo.lunghezzaB || 0) / Math.max(maxLunghezzaB, 1)) * maxScale;
                    const horizontalStartXB = poloRightX - 40;
                    const horizontalEndXB = horizontalStartXB + lunghezzaVisualeB;
                    const branchJoinXB = horizontalStartXB;
                    const branchRenderDataA = [
                        ...flattenBranchRenderData({
                        branchEntries: leftBuckets.aboveEntries,
                        sharedSide: 'A',
                        parentY: poloYLeft,
                        branchSpacing: expandedPoloSpacing,
                        joinX: branchJoinXA,
                        sourceTerminalX: horizontalEndXA,
                        branchTerminalBaseX: horizontalEndXA,
                        direction: 1,
                        rowDirection: -1
                    }),
                        ...flattenBranchRenderData({
                        branchEntries: leftBuckets.belowEntries,
                        sharedSide: 'A',
                        parentY: poloYLeft,
                        branchSpacing: expandedPoloSpacing,
                        joinX: branchJoinXA,
                        sourceTerminalX: horizontalEndXA,
                        branchTerminalBaseX: horizontalEndXA,
                        direction: 1,
                        rowDirection: 1
                    })
                    ];
                    const branchRenderDataB = [
                        ...flattenBranchRenderData({
                        branchEntries: rightBuckets.aboveEntries,
                        sharedSide: 'B',
                        parentY: poloYRight,
                        branchSpacing: expandedPoloSpacing,
                        joinX: branchJoinXB,
                        sourceTerminalX: horizontalEndXB,
                        branchTerminalBaseX: horizontalEndXB,
                        direction: -1,
                        rowDirection: -1
                    }),
                        ...flattenBranchRenderData({
                        branchEntries: rightBuckets.belowEntries,
                        sharedSide: 'B',
                        parentY: poloYRight,
                        branchSpacing: expandedPoloSpacing,
                        joinX: branchJoinXB,
                        sourceTerminalX: horizontalEndXB,
                        branchTerminalBaseX: horizontalEndXB,
                        direction: -1,
                        rowDirection: 1
                    })
                    ];

                    branchRenderDataA.forEach((branch) => {
                        const branchHovered = hoveredCable === branch.cable.originalIndex;
                        pathEntries.push({
                            key: branch.key,
                            cable: branch.cable,
                            d: branch.pathD,
                            isHovered: branchHovered,
                            opacity: branchHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                            hoverIndex: branch.cable.originalIndex
                        });
                    });

                    branchRenderDataB.forEach((branch) => {
                        const branchHovered = hoveredCable === branch.cable.originalIndex;
                        pathEntries.push({
                            key: branch.key,
                            cable: branch.cable,
                            d: branch.pathD,
                            isHovered: branchHovered,
                            opacity: branchHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                            hoverIndex: branch.cable.originalIndex
                        });
                    });

                    if ((polo.lunghezzaA || 0) > 0) {
                        pathEntries.push({
                            key: `pole-main-a-${index}`,
                            cable: polo,
                            d: buildPolylinePath([
                                { x: cableExitXA, y: cableExitYA },
                                { x: horizontalStartXA, y: poloYLeft },
                                { x: horizontalEndXA, y: poloYLeft }
                            ]),
                            isHovered,
                            opacity: isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                            hoverIndex: cable.originalIndex
                        });
                    }

                    if ((polo.lunghezzaB || 0) > 0) {
                        pathEntries.push({
                            key: `pole-main-b-${index}`,
                            cable: polo,
                            d: buildPolylinePath([
                                { x: cableExitXB, y: cableExitYB },
                                { x: horizontalStartXB, y: poloYRight },
                                { x: horizontalEndXB, y: poloYRight }
                            ]),
                            isHovered,
                            opacity: isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                            hoverIndex: cable.originalIndex
                        });
                    }

                        decorationEntries.push(
                            <g
                                key={`pole-decor-a-${index}`}
                                onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                            onMouseLeave={() => setHoveredCable(null)}
                            style={{ cursor: 'pointer' }}
                        >
                            {renderTerminalSchematic(
                                polo.terminaleA,
                                horizontalEndXA,
                                poloYLeft,
                                isMobile ? 0.03 : 0.04,
                                'A',
                                180,
                                settings.terminal_data
                            )}

                            {polo.terminaleA && polo.terminaleA !== 'NS' && (
                                <text
                                    x={horizontalEndXA}
                                    y={poloYLeft + 20}
                                    className="text-xs font-medium fill-gray-700"
                                    textAnchor="middle"
                                >
                                    {polo.terminaleA}
                                </text>
                            )}

                            {(polo.lunghezzaA || 0) > 0 && polo.siglaturaA && (
                                renderSiglaturaBadge(
                                    polo.siglaturaA,
                                    getSmartSiglaturaX({
                                        centerX: (horizontalStartXA + horizontalEndXA) / 2,
                                        terminalX: horizontalEndXA,
                                        side: 'A'
                                    }),
                                    poloYLeft,
                                    'center'
                                )
                            )}

                            {polo.collegamentoA && renderConnectionLabel(
                                polo.collegamentoA,
                                horizontalEndXA - 30,
                                poloYLeft,
                                'end',
                                horizontalEndXA - 35
                            )}
                        </g>
                    );

                    decorationEntries.push(
                        <g
                            key={`pole-decor-b-${index}`}
                            onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                            onMouseLeave={() => setHoveredCable(null)}
                            style={{ cursor: 'pointer' }}
                        >
                            {renderTerminalSchematic(
                                polo.terminaleB,
                                horizontalEndXB,
                                poloYRight,
                                isMobile ? 0.03 : 0.04,
                                'B',
                                0,
                                settings.terminal_data
                            )}

                            {polo.terminaleB && polo.terminaleB !== 'NS' && (
                                <text
                                    x={horizontalEndXB}
                                    y={poloYRight + 20}
                                    className="text-xs font-medium fill-gray-700"
                                    textAnchor="middle"
                                >
                                    {polo.terminaleB}
                                </text>
                            )}

                            {(polo.lunghezzaB || 0) > 0 && polo.siglaturaB && (
                                renderSiglaturaBadge(
                                    polo.siglaturaB,
                                    getSmartSiglaturaX({
                                        centerX: (horizontalStartXB + horizontalEndXB) / 2,
                                        terminalX: horizontalEndXB,
                                        side: 'B'
                                    }),
                                    poloYRight,
                                    'center'
                                )
                            )}

                            {polo.collegamentoB && renderConnectionLabel(
                                polo.collegamentoB,
                                horizontalEndXB + 30,
                                poloYRight,
                                'start',
                                horizontalEndXB + 35
                            )}
                        </g>
                    );

                    branchRenderDataA.forEach((branch) => {
                        const branchOpacity = hoveredCable !== null && hoveredCable !== branch.cable.originalIndex ? 0.3 : 1;
                        decorationEntries.push(
                            <g
                                key={`${branch.key}-decor`}
                                onMouseEnter={() => setHoveredCable(branch.cable.originalIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {renderIndexBadge(
                                    (branch.cable.originalIndex ?? 0) + 1,
                                    branch.branchJoinX - 20,
                                    branch.branchMidY,
                                    branchOpacity
                                )}
                                {renderTerminalSchematic(
                                    branch.terminal,
                                    branch.branchHorizontalEndX,
                                    branch.branchY,
                                    isMobile ? 0.03 : 0.04,
                                    branch.sharedSide,
                                    branch.sharedSide === 'A' ? 180 : 0,
                                    settings.terminal_data
                                )}
                                {branch.terminal && branch.terminal !== 'NS' && (
                                    <text
                                        x={branch.branchHorizontalEndX}
                                        y={branch.branchY + 20}
                                        className="text-xs font-medium fill-gray-700"
                                        textAnchor="middle"
                                    >
                                        {branch.terminal}
                                    </text>
                                )}
                                {!isEarthWire(branch.cable.colore) && renderSiglaturaBadge(
                                    branch.siglatura,
                                    getSmartSiglaturaX({
                                        centerX: (branch.branchJoinX + branch.branchHorizontalEndX) / 2,
                                        terminalX: branch.branchHorizontalEndX,
                                        side: 'A'
                                    }),
                                    branch.branchY,
                                    'center'
                                )}
                                {branch.collegamento && renderConnectionLabel(
                                    branch.collegamento,
                                    branch.branchHorizontalEndX - 30,
                                    branch.branchY,
                                    'end',
                                    branch.branchHorizontalEndX - 35
                                )}
                            </g>
                        );
                    });

                    branchRenderDataB.forEach((branch) => {
                        const branchOpacity = hoveredCable !== null && hoveredCable !== branch.cable.originalIndex ? 0.3 : 1;
                        decorationEntries.push(
                            <g
                                key={`${branch.key}-decor`}
                                onMouseEnter={() => setHoveredCable(branch.cable.originalIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {renderIndexBadge(
                                    (branch.cable.originalIndex ?? 0) + 1,
                                    branch.branchJoinX + 20,
                                    branch.branchMidY,
                                    branchOpacity
                                )}
                                {renderTerminalSchematic(
                                    branch.terminal,
                                    branch.branchHorizontalEndX,
                                    branch.branchY,
                                    isMobile ? 0.03 : 0.04,
                                    branch.sharedSide,
                                    branch.sharedSide === 'A' ? 180 : 0,
                                    settings.terminal_data
                                )}
                                {branch.terminal && branch.terminal !== 'NS' && (
                                    <text
                                        x={branch.branchHorizontalEndX}
                                        y={branch.branchY + 20}
                                        className="text-xs font-medium fill-gray-700"
                                        textAnchor="middle"
                                    >
                                        {branch.terminal}
                                    </text>
                                )}
                                {!isEarthWire(branch.cable.colore) && renderSiglaturaBadge(
                                    branch.siglatura,
                                    getSmartSiglaturaX({
                                        centerX: (branch.branchJoinX + branch.branchHorizontalEndX) / 2,
                                        terminalX: branch.branchHorizontalEndX,
                                        side: 'B'
                                    }),
                                    branch.branchY,
                                    'center'
                                )}
                                {branch.collegamento && renderConnectionLabel(
                                    branch.collegamento,
                                    branch.branchHorizontalEndX + 30,
                                    branch.branchY,
                                    'start',
                                    branch.branchHorizontalEndX + 35
                                )}
                            </g>
                        );
                    });
                });

                return (
                    <>
                        {sortPathEntriesForHover(pathEntries).map((entry) => (
                            <g
                                key={entry.key}
                                onMouseEnter={() => setHoveredCable(entry.hoverIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                <CableSVGPath
                                    cable={entry.cable}
                                    d={entry.d}
                                    isHovered={entry.isHovered}
                                    useAWG={settings.useAWG}
                                    strokeLinecap="round"
                                    opacity={entry.opacity}
                                />
                            </g>
                        ))}

                        {decorationEntries}

                        {lunghezzeUnicheA.filter(lunghezza => lunghezza > 0).map((lunghezza, index) => {
                            const primoPoloConQuesta = cable.poli.find(p => (p.lunghezzaA || 0) === lunghezza);
                            const primoPoloIndex = cable.poli.indexOf(primoPoloConQuesta);
                            const quotaY = poleLayout[primoPoloIndex]?.topVisualYLeft ?? poleLayout[primoPoloIndex]?.poloYLeft ?? poloStartYLeft;
                            const cableExitXA = cableRect.x;
                            const lunghezzaVisuale = (lunghezza / maxLunghezzaA) * maxScale;
                            const horizontalStartXA = poloLeftX + 40;
                            const horizontalEndXA = horizontalStartXA - lunghezzaVisuale;
                            const quotaLineY = quotaY - 18 - (index * 12);

                            return (
                                <g key={`quota-a-${lunghezza}`} className="opacity-70">
                                    <line x1={cableExitXA} y1={quotaLineY - 5} x2={cableExitXA} y2={quotaLineY + 5} stroke="#666" strokeWidth="0.5" />
                                    <line x1={horizontalEndXA} y1={quotaLineY - 5} x2={horizontalEndXA} y2={quotaLineY + 5} stroke="#666" strokeWidth="0.5" />
                                    <line x1={cableExitXA} y1={quotaLineY} x2={horizontalEndXA} y2={quotaLineY} stroke="#666" strokeWidth="0.5" />
                                    <text
                                        x={(cableExitXA + horizontalEndXA) / 2}
                                        y={quotaLineY - 4}
                                        className="text-xs fill-gray-600 font-medium"
                                        textAnchor="middle"
                                        fontSize="9"
                                    >
                                        {lunghezza}mm
                                    </text>
                                </g>
                            );
                        })}

                        {lunghezzeUnicheB.filter(lunghezza => lunghezza > 0).map((lunghezza, index) => {
                            const primoPoloConQuesta = cable.poli.find(p => (p.lunghezzaB || 0) === lunghezza);
                            const primoPoloIndex = cable.poli.indexOf(primoPoloConQuesta);
                            const quotaY = poleLayout[primoPoloIndex]?.topVisualYRight ?? poleLayout[primoPoloIndex]?.poloYRight ?? poloStartYRight;
                            const cableExitXB = cableRect.x + cableRect.width;
                            const lunghezzaVisuale = (lunghezza / maxLunghezzaB) * maxScale;
                            const horizontalStartXB = poloRightX - 40;
                            const horizontalEndXB = horizontalStartXB + lunghezzaVisuale;
                            const quotaLineY = quotaY - 18 - (index * 12);

                            return (
                                <g key={`quota-b-${lunghezza}`} className="opacity-70">
                                    <line x1={cableExitXB} y1={quotaLineY - 5} x2={cableExitXB} y2={quotaLineY + 5} stroke="#666" strokeWidth="0.5" />
                                    <line x1={horizontalEndXB} y1={quotaLineY - 5} x2={horizontalEndXB} y2={quotaLineY + 5} stroke="#666" strokeWidth="0.5" />
                                    <line x1={cableExitXB} y1={quotaLineY} x2={horizontalEndXB} y2={quotaLineY} stroke="#666" strokeWidth="0.5" />
                                    <text
                                        x={(cableExitXB + horizontalEndXB) / 2}
                                        y={quotaLineY - 4}
                                        className="text-xs fill-gray-600 font-medium"
                                        textAnchor="middle"
                                        fontSize="9"
                                    >
                                        {lunghezza}mm
                                    </text>
                                </g>
                            );
                        })}
                    </>
                );
            })()}

            <rect
                x={cableRect.x}
                y={cableRect.y}
                width={cableRect.width}
                height={cableRect.height}
                fill="#e5e7eb"
                stroke="#374151"
                strokeWidth="2"
                rx="8"
            />

            <g
                onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                onMouseLeave={() => setHoveredCable(null)}
                style={{ cursor: 'pointer' }}
            >
                {(() => {
                    const badgeOpacity = hoveredCable !== null && hoveredCable !== cable.originalIndex ? 0.3 : 1;
                    return (
                        <>
                <circle
                    cx={cableX + cableWidth / 2}
                    cy={cableRect.y - 25}
                    r={15}
                    fill="#1F2937"
                    opacity={0.9 * badgeOpacity}
                />
                <text
                    className="cable-number"
                    x={cableX + cableWidth / 2}
                    y={cableRect.y - 20}
                    fontSize={14}
                    fill="white"
                    textAnchor="middle"
                    opacity={badgeOpacity}
                >
                    #{cable.originalIndex + 1}
                </text>
                        </>
                    );
                })()}
            </g>

            <text
                x={cableX + cableWidth / 2}
                y={cableRect.y + cableHeight + 20}
                className="text-sm fill-gray-800"
                textAnchor="middle"
                fontSize="12"
            >
                {cable.nomeCavo} • {cable.lunghezza}mm
            </text>

            <g>
                <rect
                    x={cableRect.x + 10}
                    y={cableRect.y + cableHeight / 2 - 8}
                    width={20}
                    height={16}
                    fill="#dcfce7"
                    stroke="#16a34a"
                    strokeWidth="1"
                    rx="2"
                />
                <text
                    x={cableRect.x + 20}
                    y={cableRect.y + cableHeight / 2 + 4}
                    className="text-xs font-bold"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#15803d"
                >
                    A
                </text>
            </g>

            <rect
                x={cableX + cableWidth / 2 - 30}
                y={cableRect.y + cableHeight / 2 - 8}
                width={60}
                height={16}
                fill="#8b5cf6"
                rx="8"
            />
            <text
                x={cableX + cableWidth / 2}
                y={cableRect.y + cableHeight / 2 + 4}
                className="text-xs font-bold fill-white"
                textAnchor="middle"
                fontSize="10"
            >
                {cable.poli.length}{getMultipolarSeparator(cable.poli)}{cable.poli[0]?.sezione || 0.5}
            </text>

            <g>
                <rect
                    x={cableRect.x + cableWidth - 30}
                    y={cableRect.y + cableHeight / 2 - 8}
                    width={20}
                    height={16}
                    fill="#fecaca"
                    stroke="#dc2626"
                    strokeWidth="1"
                    rx="2"
                />
                <text
                    x={cableRect.x + cableWidth - 20}
                    y={cableRect.y + cableHeight / 2 + 4}
                    className="text-xs font-bold"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#dc2626"
                >
                    B
                </text>
            </g>
        </svg>
    );
};

export default MultipolarCableVisualization;
